import { Rate } from 'antd';
import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import { CAMPAIGN_FILE_STATUS } from 'components/campaigns/Details/constants';
import type { BaseUser } from 'components/DisplayNameFormatter';
import DisplayNameFormatter from 'components/DisplayNameFormatter';
import { AUDIT_STATUS_FILTER_LABEL } from 'modules/audits/constants';
import { CAMPAIGN_STATUS_LABEL } from 'modules/campaigns/constants';
import { FOLLOW_UP_LABEL } from 'modules/followUp/constant';
import { RELEASE_STATE_LABEL, RELEASE_TYPE_LABEL } from 'modules/releases/constants';
import { humanize, ucFirst } from 'utils/global';

import { createDictionary } from 'utils/ObjectUtils';

import { CHECKLIST_SELECT_LABEL } from './ChecklistConstants';
import { SMS_PROVIDERS_DISPLAY_NAME } from './IntegrationConstants';
import { SELF_SERVICE_TYPE } from './SelfServiceConstants';

export const TO = 'to';
export const FROM = 'from';

export const START_DATE_TO = 'startDateTo';
export const START_DATE_FROM = 'startDateFrom';

export const AUDIT_MODEL = 'AUDIT_MODEL';
export const AUDIT_STATUS = 'auditStatus';

export const MODE = 'mode';
export const MODES = 'modes';
export const AUTHORS = 'authors';
export const SENDERS = 'senders';
export const CHECK_IN_SENDERS = 'checkInSenders';
export const CHECK_OUT_SENDERS = 'checkOutSenders';
export const ANY_SENDERS = 'anySenders';
export const BRANDS = 'brands';
export const ASSIGNEE = 'assignedIds';
export const ASSIGNEE_USERS = 'assignedUserIds';
export const ASSIGNEE_USER_GROUPS = 'assignedUserGroupIds';
export const ADVISOR = 'serviceAdvisorIds';
export const ADVISOR_USERS = 'serviceAdvisorUserIds';
export const ADVISOR_USER_GROUPS = 'serviceAdvisorUserGroupIds';
export const RATINGS = 'ratings';
export const STEPS = 'stepIds';
export const PENDING_ADVICES = 'pendingAdvices';
export const KANBAN_ACTIONS = 'readySelfServices';
export const TAGS = 'tagIds';
export const MOBILITY_FLAGS = 'mobilityFlags';
export const REMINDER_WITHOUT_PRICE = 'reminderWithoutPrice';
export const CHANNELS = 'channels';
export const CAMPAIGNS_STATUS = 'campaignStatus';
export const CAMPAIGN_FILES_STATUS = 'campaignFileStatus';
export const RELEASES_STATE = 'releaseStates';
export const RELEASES_TYPE = 'releaseTypes';

export const AUTO_REFRESH = 'autoRefreshResults';
export const DISPLAY_CLOSED = 'displayClosed';
export const FOLLOWED = 'followed';

export const DEVICES_APP_VERSION = 'lastAppVersion';
export const DEVICES_OS_VERSION = 'operatingSystemVersion';
export const DEVICES_TYPES = 'deviceTypes';
export const DISPLAY_UNCOMPLETED_PAYMENT = 'displayUncompleted'; // Fake filter

export const PAYMENT_TYPES = 'paymentTypes';
export const PAYMENT_GATEWAYS = 'paymentGateways';
export const PAYMENT_PROVIDERS = 'paymentProviders';
export const PAYMENT_PROGRESS_STATUS = 'paymentStatus';

export const KIOSK_STATUS = 'kioskStatus';
export const KIOSK_TYPES = 'types';
export const KIOSK_MODELS = 'models';

export const CAMERA_STATUS = 'cameraStatus';

export const HARDWARE_STATUS = 'hardwareStatus';

export const INTERVENTION_STATUS = 'interventionStatus';
export const INTERVENTION_PAYMENT_STATUS = 'paymentStatus';

export const SELECTED_DEALER_ID = 'dealerId';
export const SELECTED_DEALER_IDS = 'dealerIds';
export const SELECTED_GROUP_ID = 'groupId';
export const SELECTED_CHECKLIST_IDS = 'originChecklistIds';

export const MODULES = 'modules';

export const CHECKLISTS = 'originChecklistIds';

export const PERIOD = 'period';
export const SEARCH = 'text';
export const SORT = 'sort';
export const SORT_DIRECTION = 'sortDirection';
export const SCHEDULABLE_TYPE = 'schedulableType';

export const ALL = 'ALL';
export const TODAY = 'TODAY';
export const WEEK = 'WEEK';
export const CUSTOM = 'CUSTOM';
export const STATUS = 'status';
export const SELECTED_DATE = 'selectedDate';

/** Fake filters name, see `src/components/ui/filters/filterPanel/SelfServiceStatus.js` */
export const FOLLOW_UP_STATUS = 'followUpStatus';
export const SELF_SERVICE_STATUS = 'selfServiceStatus';

export const SENDER_FILTER_MODE = {
  EXACT: 'exact',
  ANY: 'any',
} as const;
export type SenderFilterMode = (typeof SENDER_FILTER_MODE)[keyof typeof SENDER_FILTER_MODE];

export const NOT_EXIST = 'notExist';
export const ROLES = 'roles';
export const PROVIDERS = 'providers';
export const OVERCOST = 'overcost';

export const TYPES = 'types';
export const FAVORITE = 'favorite';
export const APPOINTMENT_TYPE = 'appointmentType';
export const APPOINTMENT_STATUS = 'appointmentStatus';

export const EMAIL_STATUS = 'emailStatus';
export const HANDLED = 'handled';
export const LINKED_INTERVENTION = 'linkedIntervention';

export const AUDIT_DASHBOARD_FILTERS = [AUDIT_STATUS, AUTHORS, ASSIGNEE_USERS];
export const CAMERA_DASHBOARD_FILTERS = [CAMERA_STATUS];
export const HARDWARE_DASHBOARD_FILTERS = [HARDWARE_STATUS];
export const KIOSK_DASHBOARD_FILTERS = [KIOSK_STATUS, KIOSK_MODELS] as const;
export const FOLLOW_UP_FILTERS = [MODULES, BRANDS, AUTHORS, SENDERS, FOLLOW_UP_STATUS, RATINGS, ADVISOR_USERS];
export const MOBILITY_FILTERS = [MOBILITY_FLAGS, MODULES, AUTHORS, BRANDS, STEPS, ASSIGNEE_USERS, ADVISOR_USERS];
export const PAYMENT_DASHBOARD_FILTERS = [PAYMENT_TYPES, PAYMENT_PROGRESS_STATUS, PAYMENT_PROVIDERS, BRANDS, MODES];

// Appointments filters
export const SAFE_BOOKING_DASHBOARD_FILTERS = [BRANDS, APPOINTMENT_TYPE];
export const APPOINTMENT_DASHBOARD_FILTERS = [APPOINTMENT_STATUS, BRANDS, APPOINTMENT_TYPE];

// Users
export const USERS_DASHBOARD_FILTERS = [ROLES];

// Campaigns and campaign files filters
export const CAMPAIGN_FILE_FILTERS = [EMAIL_STATUS, CAMPAIGN_FILES_STATUS, INTERVENTION_STATUS];
export const CAMPAIGN_FILE_SWITCH_FILTERS = [HANDLED, LINKED_INTERVENTION];

/**
 * @typedef {'pendingAdvices' | 'autoRefreshResults' | 'displayClosed' | 'followed'} InterventionFilters
 */

/** @type {Array<InterventionFilters>} */
export const INTERVENTION_FILTERS = [FOLLOWED, AUTO_REFRESH, DISPLAY_CLOSED, PENDING_ADVICES, REMINDER_WITHOUT_PRICE];

export const KANBAN_ACTIONS_DATA = {
  [`${SELF_SERVICE_TYPE.CHECK_IN}_CUSTOMER`]: {
    id: 'interventionKanban.filters.actions.retrieveCustomerKeys',
    defaultMessage: 'Retrieve customer keys'
  },
  [`${SELF_SERVICE_TYPE.CHECK_OUT}_CUSTOMER`]: {
    id: 'interventionKanban.filters.actions.prepareCustomerKeys',
    defaultMessage: 'Prepare customer keys'
  },
  [`${SELF_SERVICE_TYPE.CHECK_IN}_MOBILITY`]: {
    id: 'interventionKanban.filters.actions.prepareMobilityKeys',
    defaultMessage: 'Prepare courtesy vehicle keys'
  },
  [`${SELF_SERVICE_TYPE.CHECK_OUT}_MOBILITY`]: {
    id: 'interventionKanban.filters.actions.retrieveMobilityKeys',
    defaultMessage: 'Retrieve courtesy vehicle keys'
  }
};

const booleanEntityFormatter = (value: boolean) =>
  (value ? { id: 'yes', defaultMessage: 'Yes' } : { id: 'no', defaultMessage: 'No' });

const TYPE_LABELS: Record<'KEY_DEPOSIT' | 'KEY_RETRIEVE', MessageDescriptor> = {
  KEY_DEPOSIT: { id: 'page.keyDeposit.planning.legend.KeyDeposit', defaultMessage: 'Keys drop-off' },
  KEY_RETRIEVE: { id: 'page.keyDeposit.planning.legend.KeyRetrieve', defaultMessage: 'Keys pick up' }
};

export const FIELD_DETAILS = {
  [CAMPAIGNS_STATUS]: {
    label: { id: 'status', defaultMessage: 'Status' },
    entityFormatter: (status: string) => CAMPAIGN_STATUS_LABEL[status]
  },
  [RELEASES_STATE]: {
    label: { id: 'status', defaultMessage: 'Status' },
    entityFormatter: (status: string) => RELEASE_STATE_LABEL[status]
  },
  [RELEASES_TYPE]: {
    label: { id: 'type', defaultMessage: 'Type' },
    entityFormatter: (status: string) => RELEASE_TYPE_LABEL[status]
  },
  [AUDIT_STATUS]: {
    label: { id: 'status', defaultMessage: 'Status' },
    entityFormatter: (status: string) => AUDIT_STATUS_FILTER_LABEL[status]
  },
  [CHANNELS]: {
    label: { id: 'customSteps.table.title.channels', defaultMessage: 'Channels' },
    entityFormatter: (channel: string) => ({ id: `channels.${channel}`, defaultMessage: channel })
  },
  [MODULES]: {
    label: { id: 'page.home.filters.type.modules', defaultMessage: 'Modules' },
    entityFormatter: (value: string) => ({ id: value.toLowerCase(), defaultMessage: value })
  },
  [CHECKLISTS]: {
    label: CHECKLIST_SELECT_LABEL
  },
  [PAYMENT_TYPES]: {
    label: { id: 'page.config.dealer_general.planetvo.type', defaultMessage: 'Type' },
    entityFormatter: (value: string) => ({ id: `payments.type.${value.toLowerCase()}`, defaultMessage: value })
  },
  [PAYMENT_PROGRESS_STATUS]: {
    label: { id: 'page.stats.status', defaultMessage: 'Status' },
    entityFormatter: (value: string) => ({ id: `payments.progress_status.${value.toLowerCase()}`, defaultMessage: value })
  },
  [PAYMENT_PROVIDERS]: {
    label: { id: 'provider', defaultMessage: 'Provider' },
    entityFormatter: (value: string) => ({ id: `payment_provider.${value}`, defaultMessage: ucFirst(humanize(value)) })
  },
  [MODES]: {
    label: { id: 'mode', defaultMessage: 'Mode' },
    entityFormatter: (value: string) => ({ id: `templateMode.${value}`, defaultMessage: value })
  },
  [KANBAN_ACTIONS]: {
    label: { id: 'page.home.filters.type.kanbanAction', defaultMessage: 'Actions' },
    entityFormatter: (value: keyof typeof KANBAN_ACTIONS_DATA) => KANBAN_ACTIONS_DATA[value]
  },
  [MOBILITY_FLAGS]: {
    label: { id: 'page.home.filters.type.kanbanAction', defaultMessage: 'Actions' },
    entityFormatter: (value: string) => ({
      defaultMessage: humanize(value).toLowerCase(),
      id: `page.home.filters.mobilityFlags.${value}`
    })
  },
  [BRANDS]: {
    label: { id: 'page.home.filters.type.brand', defaultMessage: 'Brands' },
    entityFormatter: (brand: any) => brand.name
  },
  [AUTHORS]: {
    label: { id: 'page.home.filters.type.author', defaultMessage: 'Authors' },
    entityFormatter: (author: BaseUser) => author.displayName
  },
  [SENDERS]: {
    label: { id: 'page.home.filters.type.filesSentBy', defaultMessage: 'Files sent by' },
    entityFormatter: (sender: BaseUser) => <DisplayNameFormatter user={sender} />
  },
  [CHECK_IN_SENDERS]: {
    label: { id: 'page.home.filters.type.checkInSentBy', defaultMessage: 'Check-in sent by' },
    entityFormatter: (checkInSender: BaseUser) => <DisplayNameFormatter user={checkInSender} />
  },
  [CHECK_OUT_SENDERS]: {
    label: { id: 'page.home.filters.type.checkOutSentBy', defaultMessage: 'Check-out sent by' },
    entityFormatter: (checkOutSender: BaseUser) => <DisplayNameFormatter user={checkOutSender} />
  },
  [ANY_SENDERS]: {
    label: { id: 'page.home.filters.type.senders', defaultMessage: 'Senders' },
    entityFormatter: (sender: BaseUser) => <DisplayNameFormatter user={sender} />
  },
  [RATINGS]: {
    label: { id: 'page.home.filters.type.rating', defaultMessage: 'Ratings' },
    listEntityFormatter: (rating: number) => <Rate character="★" value={rating} disabled />,
    entityFormatter: (rating: number) => (
      <FormattedMessage
        id="page.fiche.rating.star"
        defaultMessage="{count, plural, one {# star} other {# stars}}"
        values={{ count: rating }}
      />
    )
  },
  [KIOSK_TYPES]: {
    label: { id: 'page.config.dealer_general.planetvo.type', defaultMessage: 'Type' },
    entityFormatter: (type: string) => ({
      id: `page.kiosks.types.${type}`,
      defaultMessage: ucFirst(humanize(type))
    })
  },
  [KIOSK_MODELS]: {
    label: { id: 'models', defaultMessage: 'Models' },
    entityFormatter: (model: string) => ({
      id: `page.kiosks.models.${model}`,
      defaultMessage: ucFirst(humanize(model))
    })
  },
  [KIOSK_STATUS]: {
    label: { id: 'status', defaultMessage: 'Status' },
    entityFormatter: (status: string) => ({
      id: `audito.page.models.status.${status}`,
      defaultMessage: ucFirst(humanize(status))
    })
  },
  [CAMERA_STATUS]: {
    label: { id: 'status', defaultMessage: 'Status' },
    entityFormatter: (status: string) => ({
      id: `audito.page.models.status.${status}`,
      defaultMessage: ucFirst(humanize(status))
    })
  },
  [HARDWARE_STATUS]: {
    label: { id: 'status', defaultMessage: 'Status' },
    entityFormatter: (status: string) => ({
      id: `audito.page.models.status.${status}`,
      defaultMessage: ucFirst(humanize(status))
    })
  },
  [SELF_SERVICE_STATUS]: {
    label: { id: 'page.home.filters.type.selfServiceStatus', defaultMessage: 'Self-service status' },
    entityFormatter: ({ label }: { label: string }) => ({
      id: `intervention_self_service_status.${label}`,
      defaultMessage: ucFirst(humanize(label))
    })
  },
  [PROVIDERS]: {
    label: { id: 'statistics_message_cost.providerFilter', defaultMessage: 'By providers' },
    entityFormatter: (value: string) => (SMS_PROVIDERS_DISPLAY_NAME as Record<string, string>)[value] ?? value
  },
  [EMAIL_STATUS]: {
    label: { id: 'page.campaigns.details.emailStatusTitle', defaultMessage: 'Email status' },
    entityFormatter: (status: string) => ({ id: `page.campaigns.details.emailStatus.${status}`, defaultMessage: status })
  },
  [CAMPAIGN_FILES_STATUS]: {
    label: { id: 'page.campaigns.details.campaignFile', defaultMessage: 'Campaign file' },
    entityFormatter: (value: string) => {
      if (value === CAMPAIGN_FILE_STATUS.PENDING) {
        return { id: value.toLowerCase(), defaultMessage: value };
      }
      return { id: `intervention_status.${value.toLowerCase()}`, defaultMessage: value };
    }
  },
  [INTERVENTION_STATUS]: {
    label: { id: 'file', defaultMessage: 'File' },
    entityFormatter: (value: string) => ({ id: `intervention_status.${value.toLowerCase()}`, defaultMessage: value })
  },
  [HANDLED]: {
    label: { id: 'handled', defaultMessage: 'Handled' },
    entityFormatter: booleanEntityFormatter
  },
  [LINKED_INTERVENTION]: {
    label: {
      id: 'page.campaigns.details.filters.linkedIntervention',
      defaultMessage: 'Display campaign files that have generated a file'
    },
    entityFormatter: booleanEntityFormatter
  },
  [FOLLOW_UP_STATUS]: {
    label: { id: 'page.followup.table.status', defaultMessage: 'Follow-up status' },
    entityFormatter: (value: keyof typeof FOLLOW_UP_LABEL) => FOLLOW_UP_LABEL[value]
  },
  [APPOINTMENT_STATUS]: {
    label: { id: 'page.home.filters.type.status', defaultMessage: 'Status' },
    entityFormatter: (value: string) => ({ id: `intervention_status.${value.toLowerCase()}`, defaultMessage: value })
  },
  [APPOINTMENT_TYPE]: {
    label: { id: 'page.home.filters.type.type', defaultMessage: 'Types' },
    entityFormatter: (value: keyof typeof TYPE_LABELS) => TYPE_LABELS[value] ?? { id: value, defaultMessage: value }
  },
  [TAGS]: {
    label: { id: 'page.config.dealer_general.tags.title', defaultMessage: 'Tags' },
    entityFormatter: (tag: any) => tag.name
  }
};

export const NOT_EXIST_DICTIONARY = createDictionary(
  {
    [STEPS]: ['stepId'],
    [ASSIGNEE]: ['assignedUserId', 'assignedUserGroupId'],
    [ADVISOR]: ['serviceAdvisorUserId', 'serviceAdvisorUserGroupId']
  },
  []
);

export const NOT_EXIST_LABEL_DESCRIPTOR = createDictionary(
  {
    [STEPS]: { id: 'customSteps.noStep', defaultMessage: 'No step' },
    [ADVISOR]: { id: 'customSteps.notAssigned', defaultMessage: 'Unassigned' },
    [ASSIGNEE]: { id: 'customSteps.notAssigned', defaultMessage: 'Unassigned' }
  },
  { id: 'page.stats.unavailable', defaultMessage: 'N/A' }
);

export const formatNotExistRequestFilter = (notExist = []) => notExist.flatMap(field => NOT_EXIST_DICTIONARY[field]);
